import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getGetProjectionQueryKey,
  getGetReconciliationQueryKey,
  getListAccountsQueryKey,
  getListCardsQueryKey,
  useCreateCard,
  useDeleteCard,
  useListCalendars,
  useListCards,
  useReplaceCard,
} from '../api/generated/fluence'
import type { AccountLedger, CreditCard, CreditCardInput } from '../api/generated/model'
import {
  blankEstimation,
  estimationDraftFrom,
  toEstimation,
  type EstimationDraft,
} from '../lib/estimation'
import { formatDateShort, formatMoney, toMajor, toMinor } from '../lib/money'
import { EstimationFields } from './EstimationFields'

type Mode = { kind: 'idle' } | { kind: 'new' } | { kind: 'edit'; id: string }

type Draft = {
  id: string
  name: string
  linkedAccountId: string
  charges: string
  currency: string
  statementDay: string
  ruleKind: 'DAYS_AFTER_STATEMENT' | 'FIXED_DAY_OF_MONTH'
  daysAfter: string
  dayOfMonth: string
  convention: 'FOLLOWING' | 'PRECEDING' | 'MODIFIED_FOLLOWING' | 'UNADJUSTED'
  calendarId: string
  creditLimit: string
  estimation: EstimationDraft
}

function blank(accountId: string, currency: string, calendarId: string): Draft {
  return {
    id: '',
    name: '',
    linkedAccountId: accountId,
    charges: '',
    currency,
    statementDay: '25',
    ruleKind: 'DAYS_AFTER_STATEMENT',
    daysAfter: '21',
    dayOfMonth: '15',
    convention: 'FOLLOWING',
    calendarId,
    creditLimit: '',
    estimation: blankEstimation(),
  }
}

function draftFrom(card: CreditCard): Draft {
  // An imported card may have neither a cycle nor a payment rule yet — that is a real
  // state, not a broken record. The form falls back to its usual defaults so the gaps can
  // be filled in here as well as through the assistant.
  const anchor = card.statementSchedule?.anchor
  return {
    id: card.id,
    name: card.name,
    linkedAccountId: card.linkedAccountId ?? '',
    charges: String(toMajor(card.chargesMinor, card.currency)),
    currency: card.currency,
    statementDay: anchor ? String(new Date(`${anchor}T00:00:00`).getDate()) : '25',
    ruleKind: card.paymentRule?.kind ?? 'DAYS_AFTER_STATEMENT',
    daysAfter: String(card.paymentRule?.daysAfter ?? 21),
    dayOfMonth: String(card.paymentRule?.dayOfMonth ?? 15),
    convention: card.convention,
    calendarId: card.calendarId,
    creditLimit: card.creditLimitMinor ? String(toMajor(card.creditLimitMinor, card.currency)) : '',
    estimation: estimationDraftFrom(card.estimation),
  }
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)
}

/**
 * Managing credit cards.
 *
 * The list shows the resolved charge and the next few payment dates rather than the parts
 * they were derived from: a statement day plus a payment rule plus a business-day
 * convention is three pieces of arithmetic away from "money leaves on the 16th", and the
 * user cares about the answer.
 */
export function CardEditor({ accounts }: { accounts: AccountLedger[] }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>({ kind: 'idle' })
  const [draft, setDraft] = useState<Draft>(blank('', 'USD', 'US'))
  const [error, setError] = useState<string | null>(null)
  const [idTouched, setIdTouched] = useState(false)

  const cards = useListCards()
  const calendars = useListCalendars()

  const refresh = async () => {
    await Promise.all(
      [
        getListCardsQueryKey(),
        getListAccountsQueryKey(),
        getGetReconciliationQueryKey(),
        getGetProjectionQueryKey().slice(0, 1),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )
  }
  const create = useCreateCard({ mutation: { onSuccess: refresh } })
  const replace = useReplaceCard({ mutation: { onSuccess: refresh } })
  const remove = useDeleteCard({ mutation: { onSuccess: refresh } })

  const list: CreditCard[] = cards.data?.status === 200 ? cards.data.data : []
  const calendarList = calendars.data?.status === 200 ? calendars.data.data : []
  const busy = create.isPending || replace.isPending || remove.isPending
  const isNew = mode.kind === 'new'

  function startNew() {
    setError(null)
    setIdTouched(false)
    const account = accounts[0]
    setDraft(blank(account?.accountId ?? '', account?.currency ?? 'USD', calendarList[0]?.id ?? 'US'))
    setMode({ kind: 'new' })
  }

  function build(): CreditCardInput | string {
    if (!draft.name.trim()) return 'Give it a name.'
    if (!draft.id.trim()) return 'An id is required.'
    const chargesMinor = toMinor(draft.charges, draft.currency)
    if (chargesMinor === null || chargesMinor < 0) return 'Expected spend must be a non-negative number.'
    const limit = draft.creditLimit.trim() ? toMinor(draft.creditLimit, draft.currency) : null
    if (draft.creditLimit.trim() && limit === null) return 'Credit limit must be a number.'
    const day = Math.min(Math.max(Number(draft.statementDay) || 25, 1), 28)

    return {
      id: draft.id.trim(),
      name: draft.name.trim(),
      linkedAccountId: draft.linkedAccountId || undefined,
      chargesMinor,
      currency: draft.currency,
      estimation: toEstimation(draft.estimation),
      statementSchedule: {
        kind: 'RECURRING',
        anchor: `${new Date().getFullYear()}-01-${String(day).padStart(2, '0')}`,
        frequency: 'MONTHLY',
        endKind: 'NEVER',
      },
      paymentRule:
        draft.ruleKind === 'DAYS_AFTER_STATEMENT'
          ? { kind: 'DAYS_AFTER_STATEMENT', daysAfter: Number(draft.daysAfter) || 21 }
          : { kind: 'FIXED_DAY_OF_MONTH', dayOfMonth: Number(draft.dayOfMonth) || 15 },
      convention: draft.convention,
      calendarId: draft.calendarId,
      creditLimitMinor: limit ?? undefined,
    }
  }

  async function submit() {
    const built = build()
    if (typeof built === 'string') {
      setError(built)
      return
    }
    setError(null)
    const response =
      mode.kind === 'edit'
        ? await replace.mutateAsync({ id: mode.id, data: built })
        : await create.mutateAsync({ data: built })
    if (response.status === 200 || response.status === 201) {
      setMode({ kind: 'idle' })
      return
    }
    setError(detail(response) ?? 'The server rejected that.')
  }

  async function confirmDelete(card: CreditCard) {
    if (!window.confirm(`Delete "${card.name}"? This cannot be undone.`)) return
    const response = await remove.mutateAsync({ id: card.id })
    if (response.status !== 204) setError(detail(response) ?? 'That card could not be deleted.')
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Credit cards</h2>
        {mode.kind === 'idle' && (
          <button type="button" className="btn-primary btn-small" onClick={startNew}>
            Add
          </button>
        )}
      </div>

      {error && <p className="cf-error">{error}</p>}

      {mode.kind !== 'idle' && (
        <form
          className="cf-form"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <div className="cf-grid">
            <label>
              <span>Name</span>
              <input
                value={draft.name}
                autoFocus
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    name: e.target.value,
                    id: isNew && !idTouched ? slugify(e.target.value) : draft.id,
                  })
                }
                placeholder="Rewards Card"
              />
            </label>
            <label>
              <span>Id</span>
              <input
                value={draft.id}
                disabled={!isNew}
                onChange={(e) => {
                  setIdTouched(true)
                  setDraft({ ...draft, id: e.target.value })
                }}
              />
            </label>
            <label>
              <span>Paid from</span>
              <select
                value={draft.linkedAccountId}
                onChange={(e) => {
                  const next = accounts.find((a) => a.accountId === e.target.value)
                  setDraft({
                    ...draft,
                    linkedAccountId: e.target.value,
                    currency: next?.currency ?? draft.currency,
                  })
                }}
              >
                <option value="">I pay it manually</option>
                {accounts.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Expected spend ({draft.currency})</span>
              <input
                value={draft.charges}
                inputMode="decimal"
                onChange={(e) => setDraft({ ...draft, charges: e.target.value })}
                placeholder="1450.00"
              />
            </label>
            <label>
              <span>Statement closes on day</span>
              <input
                value={draft.statementDay}
                inputMode="numeric"
                onChange={(e) => setDraft({ ...draft, statementDay: e.target.value })}
              />
            </label>
            <label>
              <span>Payment is due</span>
              <select
                value={draft.ruleKind}
                onChange={(e) => setDraft({ ...draft, ruleKind: e.target.value as Draft['ruleKind'] })}
              >
                <option value="DAYS_AFTER_STATEMENT">A number of days after the statement</option>
                <option value="FIXED_DAY_OF_MONTH">On a fixed day of the month</option>
              </select>
            </label>
            {draft.ruleKind === 'DAYS_AFTER_STATEMENT' ? (
              <label>
                <span>Days after</span>
                <input
                  value={draft.daysAfter}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, daysAfter: e.target.value })}
                />
              </label>
            ) : (
              <label>
                <span>Day of month</span>
                <input
                  value={draft.dayOfMonth}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, dayOfMonth: e.target.value })}
                />
              </label>
            )}
            <label>
              <span>If it lands on a non-business day</span>
              <select
                value={draft.convention}
                onChange={(e) => setDraft({ ...draft, convention: e.target.value as Draft['convention'] })}
              >
                <option value="FOLLOWING">Move to the next business day</option>
                <option value="PRECEDING">Move to the previous business day</option>
                <option value="MODIFIED_FOLLOWING">Next, but stay inside the month</option>
                <option value="UNADJUSTED">Leave it where it falls</option>
              </select>
            </label>
            <label>
              <span>Settlement calendar</span>
              <select
                value={draft.calendarId}
                onChange={(e) => setDraft({ ...draft, calendarId: e.target.value })}
              >
                {calendarList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Credit limit ({draft.currency}, optional)</span>
              <input
                value={draft.creditLimit}
                inputMode="decimal"
                onChange={(e) => setDraft({ ...draft, creditLimit: e.target.value })}
              />
            </label>
          </div>

          <EstimationFields
            draft={draft.estimation}
            setDraft={(estimation) => setDraft({ ...draft, estimation })}
            currency={draft.currency}
            fallbackLabel="Expected spend"
          />

          <div className="cf-actions">
            <button type="button" className="btn-ghost" onClick={() => setMode({ kind: 'idle' })}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : isNew ? 'Add card' : 'Save changes'}
            </button>
          </div>
        </form>
      )}

      <ul className="cf-list">
        {list.map((card) => (
          <li key={card.id}>
            <div className="cf-main">
              <span className="cf-name">{card.name}</span>
              <span className="cf-when">
                {card.linkedAccountId
                  ? `auto-paid from ${accounts.find((a) => a.accountId === card.linkedAccountId)?.name ?? card.linkedAccountId}`
                  : 'paid manually — not projected'}
                {card.nextPayments.length > 0 &&
                  ` · next ${card.nextPayments.slice(0, 3).map(formatDateShort).join(', ')}`}
              </span>
            </div>
            <span className="cf-amount is-out">
              −{formatMoney(card.chargesMinor, card.currency)}
              {card.estimation && <span className="est-tag">est</span>}
            </span>
            <div className="cf-buttons">
              <button
                type="button"
                className="btn-ghost btn-small"
                onClick={() => {
                  setError(null)
                  setIdTouched(true)
                  setDraft(draftFrom(card))
                  setMode({ kind: 'edit', id: card.id })
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn-ghost btn-small btn-danger"
                onClick={() => confirmDelete(card)}
                disabled={busy}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function detail(response: unknown): string | null {
  if (response && typeof response === 'object' && 'data' in response) {
    const data = (response as { data: unknown }).data
    if (data && typeof data === 'object' && 'detail' in data) {
      const value = (data as { detail: unknown }).detail
      if (typeof value === 'string') return value
    }
  }
  return null
}
